import { useMemo } from "react";
import { Card, Typography, Radio, Space, Divider, Tag } from "antd";
import { CrownOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

interface TermResult {
  label: string;
  months: number;
  interestRate: number;
  monthlyPayment: number;
  totalInterest: number;
  totalPayable: number;
}

type CustomerViewCardProps = {
  unitPrice: number;
  downpayment: number;
  terms: TermResult[];
  selectedTermIndex: number;
  onSelectTerm: (index: number) => void;
  isDirty?: boolean;
};

const currencyFormat = (value: number) => {
  if (!value) {
    return '-';
  }
  
  return `₱${value.toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export const CustomerViewCard = ({
  unitPrice,
  downpayment,
  terms,
  selectedTermIndex,
  onSelectTerm,
  isDirty,
}: CustomerViewCardProps) => {
  const lowestIndex = useMemo(() => {
    if (terms.length === 0) return -1;
    let minIdx = 0;
    terms.forEach((t, i) => {
      if (t.monthlyPayment < terms[minIdx].monthlyPayment) minIdx = i;
    });
    return minIdx;
  }, [terms]);

  if (terms.length === 0) {
    return (
      <Card
        style={{
          borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          textAlign: "center",
          padding: "48px 24px",
        }}
      >
        <Text type="secondary" style={{ fontSize: "15px" }}>
          Select a vehicle and provider to see calculations.
        </Text>
      </Card>
    );
  }

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        overflow: "hidden",
        position: "relative",
      }}
      bodyStyle={{ padding: 0 }}
    >
      {isDirty && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(4px)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <div style={{ background: "#fff", padding: "16px 24px", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", border: "1px solid #d6dee8" }}>
            <Text strong style={{ color: "#1963a3ff", fontSize: "15px" }}>
              Parameter has been changed, click the calculate button to load up new computations
            </Text>
          </div>
        </div>
      )}
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
          padding: "28px 32px 24px",
          textAlign: "center",
          color: "white",
        }}
      >
        <Title
          level={4}
          style={{
            color: "white",
            margin: 0,
            fontWeight: 700,
            letterSpacing: "-0.3px",
          }}
        >
          Car Loan Calculator
        </Title>
        <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px" }}>
          Customer-facing estimation
        </Text>
      </div>

      {/* Summary Row */}
      <div style={{ padding: "28px 32px 0" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 0",
            borderBottom: "1px solid #d6dee8",
          }}
        >
          <Text style={{ fontSize: "15px", color: "#64748b" }}>
            Unit Price
          </Text>
          <Title
            level={4}
            style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}
          >
            {currencyFormat(unitPrice)}
          </Title>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 0",
            borderBottom: "1px solid #d6dee8",
          }}
        >
          <Text style={{ fontSize: "15px", color: "#64748b" }}>
            All-in Downpayment
          </Text>
          <Title
            level={4}
            style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}
          >
            {currencyFormat(downpayment)}
          </Title>
        </div>
      </div>

      {/* Terms Section */}
      <div style={{ padding: "24px 32px 32px" }}>
        <Text
          style={{
            display: "block",
            textAlign: "center",
            fontSize: "14px",
            color: "#64748b",
            marginBottom: "16px",
          }}
        >
          Choose your monthly
        </Text>

        <Radio.Group
          value={selectedTermIndex}
          onChange={(e) => onSelectTerm(e.target.value)}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size={0}>
            {terms.map((term, idx) => {
              const isSelected = idx === selectedTermIndex;
              const isLowest = idx === lowestIndex;
              return (
                <div
                  key={idx}
                  onClick={() => onSelectTerm(idx)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 20px",
                    borderRadius: idx === 0 ? "12px 12px 0 0" : idx === terms.length - 1 ? "0 0 12px 12px" : "0",
                    background: isSelected
                      ? "linear-gradient(135deg, #f0f2ff 0%, #e8eaff 100%)"
                      : "#fbfcfd",
                    border: isSelected
                      ? "2px solid #334155"
                      : "1px solid #d6dee8",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    position: "relative",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    <Radio value={idx} />
                    <div>
                      <Text
                        strong
                        style={{
                          fontSize: isSelected ? "17px" : "15px",
                          color: "#1e293b",
                          display: "block",
                        }}
                      >
                        {term.label}
                      </Text>
                      <Text
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                        }}
                      >
                        {term.months} months
                      </Text>
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    {isLowest && (
                      <Tag
                        color="gold"
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          marginBottom: "4px",
                          display: "block",
                          textAlign: "right",
                          border: "none",
                        }}
                      >
                        <CrownOutlined /> LOWEST MONTHLY
                      </Tag>
                    )}
                    <Text
                      strong
                      style={{
                        fontSize: isSelected ? "20px" : "16px",
                        color: "#1e293b",
                      }}
                    >
                      {currencyFormat(term.monthlyPayment)}
                    </Text>
                    <Text
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        marginLeft: "4px",
                      }}
                    >
                      /mo
                    </Text>
                  </div>
                </div>
              );
            })}
          </Space>
        </Radio.Group>

        <Divider style={{ margin: "24px 0 16px" }} />

        <Text
          style={{
            display: "block",
            textAlign: "center",
            fontSize: "11px",
            color: "#bfbfbf",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            fontWeight: 600,
          }}
        >
          Calculations are estimates and for approval.
        </Text>
      </div>
    </Card>
  );
};
